import { toPng } from 'html-to-image';

export interface SocialCardData {
  type: 'signal' | 'portfolio' | 'achievement';
  title: string;
  subtitle?: string;
  stats: Array<{ label: string; value: string; positive?: boolean }>;
  brandColor?: string;
}

/**
 * Generates a social card image from data
 * @param data - Card data to render
 * @returns Promise<string> - Base64 encoded image data URL
 */
export const generateSocialCard = async (
  data: SocialCardData
): Promise<string> => {
  // Create a temporary container for rendering
  const container = document.createElement('div');
  container.style.position = 'absolute';
  container.style.left = '-9999px';
  container.style.width = '1200px';
  container.style.height = '630px';
  document.body.appendChild(container);

  // Create social card HTML
  container.innerHTML = `
    <div style="
      width: 1200px;
      height: 630px;
      background: linear-gradient(135deg, #1A1A2E 0%, #16213E 50%, #0F3460 100%);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 60px;
      box-sizing: border-box;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
    ">
      <!-- Logo Section -->
      <div style="
        position: absolute;
        top: 40px;
        left: 60px;
        display: flex;
        align-items: center;
        gap: 15px;
      ">
        <div style="
          width: 50px;
          height: 50px;
          background: #00F0FF;
          clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
        "></div>
        <span style="
          font-size: 32px;
          font-weight: 700;
          color: white;
          letter-spacing: -1px;
        ">Signaly</span>
      </div>

      <!-- Main Content -->
      <div style="text-align: center; margin-top: 40px;">
        <div style="
          display: inline-block;
          padding: 12px 24px;
          background: rgba(0, 240, 255, 0.1);
          border: 1px solid rgba(0, 240, 255, 0.3);
          border-radius: 8px;
          color: #00F0FF;
          font-size: 20px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 30px;
        ">${data.type === 'signal' ? '🎯 LIVE SIGNAL' : data.type === 'portfolio' ? '💼 PORTFOLIO' : '🏆 ACHIEVEMENT'}</div>
        
        <h1 style="
          font-size: 72px;
          font-weight: 800;
          color: white;
          margin: 0 0 20px 0;
          line-height: 1.1;
        ">${data.title}</h1>
        
        ${data.subtitle ? `<p style="
          font-size: 32px;
          color: rgba(255, 255, 255, 0.7);
          margin: 0 0 50px 0;
        ">${data.subtitle}</p>` : ''}
      </div>

      <!-- Stats Grid -->
      <div style="
        display: grid;
        grid-template-columns: repeat(${Math.min(data.stats.length, 3)}, 1fr);
        gap: 30px;
        width: 100%;
        max-width: 900px;
        margin-top: 40px;
      ">
        ${data.stats
          .map(
            (stat) => `
          <div style="
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            padding: 25px;
            text-align: center;
          ">
            <div style="
              font-size: 18px;
              color: rgba(255, 255, 255, 0.6);
              margin-bottom: 8px;
              text-transform: uppercase;
              letter-spacing: 1px;
            ">${stat.label}</div>
            <div style="
              font-size: 42px;
              font-weight: 700;
              color: ${stat.positive === true ? '#00F0FF' : stat.positive === false ? '#FF4757' : 'white'};
            ">${stat.value}</div>
          </div>
        `
          )
          .join('')}
      </div>

      <!-- Footer -->
      <div style="
        position: absolute;
        bottom: 40px;
        right: 60px;
        font-size: 18px;
        color: rgba(255, 255, 255, 0.5);
        display: flex;
        align-items: center;
        gap: 10px;
      ">
        <span>🚀 Powered by AI Trading Signals</span>
      </div>
    </div>
  `;

  try {
    // Generate image from HTML
    const dataUrl = await toPng(container, {
      quality: 1.0,
      pixelRatio: 2,
      backgroundColor: '#1A1A2E',
    });

    // Clean up
    document.body.removeChild(container);

    return dataUrl;
  } catch (error) {
    // Clean up on error
    document.body.removeChild(container);
    throw new Error(`Failed to generate social card: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Generate social card for a trading signal
 */
export const generateSignalCard = async (signal: {
  pair: string;
  type: 'BUY' | 'SELL';
  confidence: number;
  profit?: number;
  status: string;
}): Promise<string> => {
  const cardData: SocialCardData = {
    type: 'signal',
    title: signal.pair,
    subtitle: `${signal.type} Signal`,
    stats: [
      { label: 'Confidence', value: `${signal.confidence}%`, positive: true },
      { label: 'Status', value: signal.status },
      ...(signal.profit != null
        ? [
            {
              label: 'Profit',
              value: `${signal.profit >= 0 ? '+' : ''}${signal.profit.toFixed(2)}%`,
              positive: signal.profit >= 0,
            },
          ]
        : []),
    ],
  };

  return generateSocialCard(cardData);
};

/**
 * Generate social card for portfolio achievement
 */
export const generatePortfolioCard = async (portfolio: {
  totalValue: number;
  totalProfitLoss: number;
  totalProfitLossPercentage: number;
}): Promise<string> => {
  const cardData: SocialCardData = {
    type: 'portfolio',
    title: `$${portfolio.totalValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    subtitle: 'Portfolio Value',
    stats: [
      {
        label: 'Total P/L',
        value: `$${portfolio.totalProfitLoss.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        positive: portfolio.totalProfitLoss >= 0,
      },
      {
        label: 'Return',
        value: `${portfolio.totalProfitLossPercentage >= 0 ? '+' : ''}${portfolio.totalProfitLossPercentage.toFixed(2)}%`,
        positive: portfolio.totalProfitLossPercentage >= 0,
      },
    ],
  };

  return generateSocialCard(cardData);
};

/**
 * Generate social card for trading milestone
 */
export const generateMilestoneCard = async (milestone: {
  title: string;
  description: string;
  value: string;
}): Promise<string> => {
  const cardData: SocialCardData = {
    type: 'achievement',
    title: milestone.title,
    subtitle: milestone.description,
    stats: [{ label: 'Achievement', value: milestone.value }],
  };

  return generateSocialCard(cardData);
};